import type {
  GarmentTemplate,
  RenderGarmentRequest,
  RenderJobSubmitResponse,
  RenderJobResponse,
  PriceCheckRequest,
  MockupPriceResponse,
  ProductRendersResponse,
  SurfaceRenderResult,
} from '@printtat/shared';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

export const mockupApi = {
  async listGarments(): Promise<GarmentTemplate[]> {
    const res = await fetch(`${API_URL}/mockup/garments`);
    if (!res.ok) throw new Error('Failed to fetch garment templates');
    return res.json();
  },

  async getGarment(garmentId: string): Promise<GarmentTemplate> {
    const res = await fetch(
      `${API_URL}/mockup/garments/${encodeURIComponent(garmentId)}`,
    );
    if (!res.ok) throw new Error('Failed to fetch garment template');
    return res.json();
  },

  async priceCheck(request: PriceCheckRequest): Promise<MockupPriceResponse> {
    const res = await fetch(`${API_URL}/mockup/price-check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!res.ok) throw new Error('Failed to check price');
    return res.json();
  },

  async render(
    request: RenderGarmentRequest,
  ): Promise<RenderJobSubmitResponse> {
    const res = await fetch(`${API_URL}/mockup/render`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!res.ok) throw new Error('Failed to submit render job');
    return res.json();
  },

  async getRenderJobStatus(jobId: string): Promise<RenderJobResponse> {
    const res = await fetch(
      `${API_URL}/mockup/render/${encodeURIComponent(jobId)}`,
    );
    if (!res.ok) throw new Error('Failed to fetch render job status');
    return res.json();
  },

  async getProductRenders(productId: string): Promise<ProductRendersResponse> {
    const res = await fetch(
      `${API_URL}/mockup/products/${encodeURIComponent(productId)}/renders`,
    );
    if (!res.ok) throw new Error('Failed to fetch product renders');
    return res.json();
  },

  async getDesignRenders(
    designId: string,
  ): Promise<{ designId: string; surfaces: SurfaceRenderResult[] }> {
    const res = await fetch(
      `${API_URL}/mockup/designs/${encodeURIComponent(designId)}/renders`,
    );
    if (!res.ok) throw new Error('Failed to fetch design renders');
    return res.json();
  },

  async isMockupCurrent(
    designId: string,
    surfaceExports: Record<string, string>,
  ): Promise<boolean> {
    const res = await fetch(
      `${API_URL}/mockup/designs/${encodeURIComponent(designId)}/is-current`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ surfaceExports }),
      },
    );
    if (!res.ok) return false;
    const data = await res.json();
    return data.current === true;
  },
};
